<template>
	<div class="new-button-menu" :class="{ 'new-button-menu--mobile absolute top-0 right-4' : isMobile }">
		<ElMenu
			ref="menu"
			mode="horizontal"
			menu-trigger="click"
			unique-opened
		>
			<ElSubmenu
				v-click-outside="clickOutside"
				index="new-button-menu"
				data-ignore-directive
				tabindex="-1"
				popper-class="flyout-menu new-button-menu__flyout flyout-menu--new-button-menu"
				:popper-append-to-body="!isMobile"
			>
				<!-- New+ Button -->
				<template #title>
					<button
						ref="newButton"
						tabindex="-1"
						data-testid="new-button-toggle"
						class="new-button-menu__button p-3 flex flex--align gap-3 text-intense border font-bold"
						:class="isMobile ? 'new-button-menu__button--mobile' : 'bg-transparent'"
					>
						<Inline svg="plus" />
						{{ newButtonText }}
					</button>
				</template>

				<!-- Form Button-->
				<CFlyoutMenuItem
					:item="getMenuOptionById('form')"
					@click="handleNewFormClick()"
				/>

				<!-- Person Form Button-->
				<CFlyoutMenuItem
					:item="getMenuOptionById('person-form')"
					@click="handlePersonFormClick()"
				/>

				<!-- Folder and User Buttons-->
				<template v-if="!canOnlyCreateForms">
					<CFlyoutMenuItem
						:item="getMenuOptionById('folder')"
						@click="handleClick('Folder')"
					/>
					<CFlyoutMenuItem
						:item="getMenuOptionById('user')"
						@click="handleClick('User')"
					/>
				</template>
			</ElSubmenu>
		</ElMenu>

		<!-- Dialogs -->
		<CCreateFormContainer
			:show="showCreateFormDialog"
			@closed="onCreateFormDialogClosed"
		/>
		<CInviteUserDialog
			:show="showInviteUserDialog"
			:current-user-global-permission="clientRole"
			@close="showInviteUserDialog = false"
		/>
		<CUpsellDialog
			v-if="showUpsellDialog"
			:show="showUpsellDialog"
			:org-code="orgCode"
			:feature="upsellFeature"
			@close="showUpsellDialog = false"
		/>
		<CCreatePersonFormContainer
			v-if="!personSubmissions"
			:show="showCreateNewPersonFormDialog"
			:org-code="orgCode"
			@close="showCreateNewPersonFormDialog = false"
		/>
		<CCreatePersonFormDialog
			v-if="personSubmissions"
			:show="showCreateNewPersonFormDialog"
			:org-code="orgCode"
			@closed="onPersonFormDialogClosed"
		/>
	</div>
</template>

<script lang="ts">
	import { defineComponent, ref, computed, watch, inject, onMounted, onBeforeUnmount, type PropType } from 'vue';
	import { Menu, Submenu } from '@cognitoforms/element-ui';
	import { isMobileKey } from 'src/utilities/injection-keys';
	import { useContextMenuInstance } from './context-menu/use-context-menu';
	import type { UpsellFeature } from 'src/components/dialogs/UpsellDialog.vue';
	import type { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import type { FlyoutMenuItem } from 'src/components/app/FlyoutMenuItem.vue';
	import CFlyoutMenuItem from 'src/components/app/FlyoutMenuItem.vue';
	import CInviteUserDialog from 'src/components/dialogs/InviteUserDialog.vue';
	import CCreateFormContainer from './new-form/CreateFormContainer.vue';
	import CUpsellDialog from 'src/components/dialogs/UpsellDialog.vue';
	import CCreatePersonFormContainer from 'src/components/dialogs/CreatePersonFormContainer.vue';
	import CCreatePersonFormDialog from 'src/components/dialogs/CreatePersonFormDialog.vue';
	import clickOutside from 'src/utilities/clickOutside';
	import session from '@cognitoforms/api/services/session';

	type NewButtonMenuItem = FlyoutMenuItem & { upsellFeature?: UpsellFeature };

	const menuItems: NewButtonMenuItem[] = [
		{
			id: 'form',
			name: 'Form',
			icon: 'form',
			handleWith: 'emit'
		},
		{
			id: 'person-form',
			name: 'Person Form',
			icon: 'person',
			handleWith: 'emit'
		},
		{
			id: 'folder',
			name: 'Folder',
			icon: 'folder',
			upsellFeature: 'folders',
			handleWith: 'emit'
		},
		{
			id: 'user',
			name: 'User',
			icon: 'users',
			upsellFeature: 'users',
			handleWith: 'emit'
		}
	];

	export default defineComponent({
		name: 'CNewButtonMenu',
		components: {
			ElMenu: Menu,
			ElSubmenu: Submenu,
			CFlyoutMenuItem,
			CCreateFormContainer,
			CInviteUserDialog,
			CUpsellDialog,
			CCreatePersonFormContainer,
			CCreatePersonFormDialog
		},
		directives: { clickOutside },
		props: {
			orgCode: {
				type: String,
				required: true
			},
			canCreateFolders: {
				type: Boolean,
				required: true
			},
			canInviteUsers: {
				type: Boolean,
				required: true
			},
			canOnlyCreateForms: {
				type: Boolean,
				required: true
			},
			clientRole: {
				type: String as PropType<ClientRole>,
				default: null
			}
		},
		emits: ['handling-interaction', 'create-folder'],
		setup(props, { emit }) {
			const isMobile = inject(isMobileKey);
			const ctxMenuInstance = useContextMenuInstance('new-button-menu');
			const personSubmissions = computed(() => session.value?.enabledFeatures?.PersonSubmissions ?? false);
			const upsellFeature = ref<UpsellFeature>(null);
			const menu = ref<Menu>(null);
			const newButton = ref<HTMLButtonElement>();
			const newMenuShown = computed(() => menu.value?.openedMenus.indexOf('new-button-menu') !== -1);
			const newButtonText = computed(() => (!isMobile.value ? `New${props.canOnlyCreateForms ? ' Form' : ''}` : null));

			// Dialog States
			const showUpsellDialog = ref(false);
			const showInviteUserDialog = ref(false);
			const showCreateFormDialog = ref(false);
			const showCreateNewPersonFormDialog = ref(false);
			let dialogRestoreCallback = null;

			watch(newMenuShown, (isShown) => {
				emit('handling-interaction', isShown);
				if (isShown)
					ctxMenuInstance.closeOtherInstances();
			});

			const checkIfSaved = (callback) => {
				// Call Cognito.navigate if it is on the window to prompt unsaved changes warning
				if (window.Cognito?.navigate)
					window.Cognito.navigate(true).then((restoreCallback) => {
						dialogRestoreCallback = restoreCallback;
						callback();
					});
				else
					callback();
			};

			const restoreChangesIfDiscarded = () => {
				if (dialogRestoreCallback) {
					dialogRestoreCallback();
					dialogRestoreCallback = null;
				}
			};

			const closeMenus = (index, event) => {
				// Don't close all menus if closing submenu or clicking within the menu (scrollbar)
				const className = event?.target?.className;

				if (event?.key !== 'Escape') {
					if (index !== 'new-button-menu')
						return;
					else if (className?.includes && (className.includes('flyout-menu') || className.includes('el-submenu')))
						return;
				}

				if (menu.value) {
					menu.value.close('create-form');
					menu.value.close('new-button-menu');
					emit('handling-interaction', false);
				}
			};

			const handleEscape = (e) => {
				if (e.key === 'Escape') {
					if (newMenuShown.value) {
						e.stopPropagation();
						closeMenus('new-button-menu', e);
						setTimeout(() => newButton.value?.focus());
					}
				}
			};

			const clickOutside = (event) => {
				if (newMenuShown.value)
					closeMenus('new-button-menu', event);
			};

			const getMenuOptionById = (id) => {
				const item = menuItems.find(o => o.id === id);
				if (id === 'person-form' && personSubmissions.value)
					return { ...item, icon: 'person-form' };
				return item;
			};

			const handleClick = (item) => {
				const option = menuItems.find(option => option.name === item);

				switch (item) {
				case 'Folder': {
					if (!props.canCreateFolders) {
						upsellFeature.value = option.upsellFeature;
						showUpsellDialog.value = true;
					}
					else
						emit('create-folder');

					break;
				}
				case 'User': {
					if (!props.canInviteUsers) {
						upsellFeature.value = option.upsellFeature;
						showUpsellDialog.value = true;
					}
					else
						showInviteUserDialog.value = true;
					break;
				}
				default:
					return null;
				}
			};

			const handleNewFormClick = () => {
				checkIfSaved(() => (showCreateFormDialog.value = true));
			};

			// New Person Form
			const handlePersonFormClick = () => {
				checkIfSaved(() => (showCreateNewPersonFormDialog.value = true));
			};

			const onCreateFormDialogClosed = () => {
				showCreateFormDialog.value = false;
				restoreChangesIfDiscarded();
			};

			const onPersonFormDialogClosed = () => {
				showCreateNewPersonFormDialog.value = false;
				restoreChangesIfDiscarded();
			};

			onMounted(() => {
				document.addEventListener('keyup', handleEscape);
			});

			onBeforeUnmount(() => {
				document.removeEventListener('keyup', handleEscape);
			});

			return {
				isMobile,
				menuItems,
				menu,
				newButton,
				newMenuShown,
				personSubmissions,
				newButtonText,
				showCreateNewPersonFormDialog,
				showCreateFormDialog,
				showUpsellDialog,
				showInviteUserDialog,
				upsellFeature,

				handleEscape,
				getMenuOptionById,
				clickOutside,
				closeMenus,
				handleNewFormClick,
				handlePersonFormClick,
				handleClick,
				onCreateFormDialogClosed,
				onPersonFormDialogClosed
			};
		}
	});
</script>
<style lang="scss" src="@cognitoforms/styles/app/flyout.scss"></style>

<style lang="scss">
.new-button-menu {

	&--mobile {
		transform: translateY(calc(-100% - $space__4));

		.new-button-menu__flyout {
			bottom: 100% !important;
			left: unset !important;
			right: 0;
			top: unset !important;
		}

		.new-button-menu__button {
			background-color: var(--bg-cta);
			border: 0;
			border-radius: 50%;
			padding: $space__button-y $space__button-x;

			.icon {
				width: $size__icon--xl;
				height: $size__icon--xl;
				color: var(--text-cta);
			}
		}
	}

	&__button {
		border-color: var(--text-intense);
		transition: background-color $speed__hovered;

		.i-simple {
			color: var(--icon-intense);
		}

		&:hover:not(&--mobile) {
			background: var(--accent-3);
		}
	}

	:root &__flyout {
		transform-origin: bottom !important;
	}
}
</style>
